import { createClient } from "@sanity/client";
import type { CaseStudy as SanityCaseStudy } from "@tennr/frontend/sanity.types";
// returns the current date in YYYY-MM-DD format
export const getLatestSanityApiVersion = () => new Date().toISOString().slice(0, 10);

// these values should come from env via .env/.env.local files
export const sanityClient = createClient({
  projectId: import.meta.env.VITE_SANITY_PROJECT_ID || "cb2fptrj",
  dataset: import.meta.env.VITE_SANITY_DATASET || "production",
  useCdn: true, // `false` if you want to ensure fresh data
  apiVersion: getLatestSanityApiVersion(), // use current date in (YYYY-MM-DD) to target the latest API
  token: import.meta.env.VITE_SANITY_READ_TOKEN, // in case we use private data sets
});

// GROQ query to fetch a single case study by slug (supports both v1 and v2)
export const getCaseStudyBySlug = async (slug: string): Promise<SanityCaseStudy | null> => {
  // First try to fetch from caseStudyV2
  // For v2, we need to map heroSection.heading to title for backward compatibility
  const queryV2 = `*[_type == "caseStudyV2" && slug.current == $slug][0] {
    ...,
    "title": heroSection.heading
  }`;

  try {
    let caseStudy = await sanityClient.fetch(queryV2, { slug });

    // If not found in v2, try v1
    if (!caseStudy) {
      const queryV1 = `*[_type == "caseStudy" && slug.current == $slug][0]`;
      caseStudy = await sanityClient.fetch(queryV1, { slug });
    }

    return caseStudy;
  } catch (error) {
    console.error("Error fetching case study:", error);
    return null;
  }
};

// GROQ query to fetch all case studies (supports both v1 and v2)
export const getAllCaseStudies = async (): Promise<SanityCaseStudy[]> => {
  // For v2, we need to map heroSection.heading to title for backward compatibility
  const queryV2 = `*[_type == "caseStudyV2"] {
    ...,
    "title": heroSection.heading
  } | order(order asc, publishedAt desc)`;

  try {
    const caseStudiesV2 = await sanityClient.fetch(queryV2);

    // Combine and sort by order, then publishedAt
    const allCaseStudies = caseStudiesV2;
    return allCaseStudies.sort((a, b) => {
      if (a.order !== b.order) return (a.order || 0) - (b.order || 0);
      return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
    });
  } catch (error) {
    console.error("Error fetching case studies:", error);
    return [];
  }
};

// helper to get Sanity image URL
export const getImageUrl = (imageRef: any) => {
  if (!imageRef?.asset?._ref) return null;

  const projectId = import.meta.env.VITE_SANITY_PROJECT_ID || "your-project-id";
  const dataset = import.meta.env.VITE_SANITY_DATASET || "production";

  // Extract the image ID from the reference
  // Format: image-Tb9Ew8CXIwaY6R1kjMvI0uRR-2000x3000-jpg
  const [_1, id, dimensions, format] = imageRef.asset._ref.split("-");

  return `https://cdn.sanity.io/images/${projectId}/${dataset}/${id}-${dimensions}.${format}`;
};
